import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Chip,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  Share as ShareIcon,
  Assessment as AnalyticsIcon,
  Visibility as ViewIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useAuth } from '../contexts/AuthContext';
import { copyToClipboard, showCopyPrompt, isClipboardAvailable } from '../utils/clipboard';
import { filterFieldsByVisibility, getFieldVisibilityInfo } from '../utils/fieldVisibility';
import axios from 'axios';

interface FormField {
  id: string;
  name: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  order: number;
  visibility?: {
    level?: 'public' | 'admin-only' | 'hidden';
    publicCanView?: boolean;
    description?: string;
  };
}

interface FormData {
  id: number | string;
  title: string;
  description: string;
  fields: FormField[];
  isActive: boolean;
  customUrl?: string;
  settings: {
    requirePayment?: boolean;
    amount?: number;
    paymentAmount?: number;
    redirectUrl?: string;
    allowMultipleSubmissions?: boolean;
  };
  submissionCount: number;
  analytics?: any;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: number;
    username: string;
    email: string;
  };
}

const FormView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [form, setForm] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editUrlDialog, setEditUrlDialog] = useState(false);
  const [newCustomUrl, setNewCustomUrl] = useState('');
  const [urlError, setUrlError] = useState('');

  const fetchForm = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Form view API response:', response.data); // Debug log
      setForm(response.data);
    } catch (error) {
      console.error('Error fetching form:', error);
      showSnackbar('Failed to fetch form', 'error');
      navigate('/admin/forms');
    } finally {
      setLoading(false);
    }
  }, [id, showSnackbar, navigate]);

  useEffect(() => {
    if (id) {
      fetchForm();
    }
  }, [id, fetchForm]);

  const handleCopyLink = async () => {
    const publicUrl = form?.customUrl 
      ? `${window.location.origin}/${form.customUrl}`
      : `${window.location.origin}/form/${id}`;
    
    try {
      const success = await copyToClipboard(publicUrl);
      if (success) {
        showSnackbar('Form link copied to clipboard! 📋', 'success');
      } else {
        // Show prompt as fallback for non-secure contexts
        showCopyPrompt(publicUrl);
        showSnackbar('Copy the link from the popup dialog', 'info');
      }
    } catch (error) {
      console.error('Copy failed:', error);
      showCopyPrompt(publicUrl);
      showSnackbar('Please copy the link manually from the dialog', 'warning');
    }
  };

  const handleEditUrl = () => {
    setNewCustomUrl(form?.customUrl || '');
    setUrlError('');
    setEditUrlDialog(true);
  };

  const handleSaveUrl = async () => {
    if (!newCustomUrl.trim()) {
      setUrlError('Custom URL cannot be empty');
      return;
    }

    // Basic URL validation
    const urlPattern = /^[a-zA-Z0-9-_]+$/;
    if (!urlPattern.test(newCustomUrl)) {
      setUrlError('URL can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/forms/${id}/url`,
        { customUrl: newCustomUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setForm(prev => prev ? { ...prev, customUrl: newCustomUrl } : null);
      setEditUrlDialog(false);
      showSnackbar('Custom URL updated successfully', 'success');
    } catch (error: any) {
      console.error('Error updating custom URL:', error);
      if (error.response?.status === 409) {
        setUrlError('This URL is already in use. Please choose a different one.');
      } else {
        setUrlError('Failed to update URL. Please try again.');
      }
    }
  };

  const handleTogglePublish = async () => {
    if (!form) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/forms/${id}`,
        { isActive: !form.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setForm({ ...form, isActive: !form.isActive });
      showSnackbar(
        `Form ${!form.isActive ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
    } catch (error) {
      console.error('Error updating form:', error);
      showSnackbar('Failed to update form', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading form...</Typography>
      </Box>
    );
  }

  if (!form) {
    console.log('Form is null or undefined'); // Debug log
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6">Form not found</Typography>
        <Button onClick={() => navigate('/admin/forms')} sx={{ mt: 2 }}>
          Back to Forms
        </Button>
      </Box>
    );
  }

  console.log('Rendering form:', form); // Debug log

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Typography variant="h4">{form.title}</Typography>
            <Chip
              label={form.isActive ? 'Active' : 'Inactive'}
              color={form.isActive ? 'success' : 'default'}
            />
          </Box>
          <Typography variant="body1" color="textSecondary">
            {form.description}
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          {/* Admin-only action buttons - only show in admin context */}
          {[
            'super_admin', 'admin', 'form_manager', 'payment_approver',
            'submission_viewer', 'submission_editor', 'notification_manager'
          ].includes(user?.role || '') && window.location.pathname.includes('/admin/') && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/admin/forms/${id}/edit`)}
              >
                Edit Form
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleCopyLink}
              >
                Copy Link
              </Button>
              <Button
                variant="outlined"
                startIcon={<AnalyticsIcon />}
                onClick={() => navigate(`/admin/submissions?formId=${id}`)}
              >
                View Submissions
              </Button>
              <Button
                variant={form.isActive ? 'outlined' : 'contained'}
                onClick={handleTogglePublish}
              >
                {form.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          )}
          
          {/* Public context - show only public view toggle */}
          {!window.location.pathname.includes('/admin/') && (
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={handleCopyLink}
            >
              Share Form
            </Button>
          )}
        </Box>
      </Box>

      <Box display="flex" gap={3}>
        {/* Form Analytics */}
        <Box flex={1}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Analytics
            </Typography>
            
            <Box display="flex" gap={4}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {form.submissionCount || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Submissions
                </Typography>
              </Box>
              
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {form.fields.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Fields
                </Typography>
              </Box>

              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {filterFieldsByVisibility(form.fields, user, 'view').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Visible Fields
                </Typography>
              </Box>

              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {(form.settings?.requirePayment || form.settings?.requirePayment) ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Payment Required
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Form Settings */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Settings
            </Typography>
            
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Payment Required</Typography>
                <Chip
                  label={(form.settings?.requirePayment || form.settings?.requirePayment) ? 'Yes' : 'No'}
                  color={(form.settings?.requirePayment || form.settings?.requirePayment) ? 'primary' : 'default'}
                />
              </Box>

              {(form.settings?.requirePayment || form.settings?.requirePayment) && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography>Payment Amount</Typography>
                  <Typography fontWeight="bold">
                    ৳{form.settings?.amount || form.settings?.paymentAmount || 0}
                  </Typography>
                </Box>
              )}

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Multiple Submissions</Typography>
                <Chip
                  label={form.settings?.allowMultipleSubmissions ? 'Allowed' : 'Not Allowed'}
                  color={form.settings?.allowMultipleSubmissions ? 'success' : 'default'}
                />
              </Box>

              {form.settings?.redirectUrl && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography>Redirect URL</Typography>
                  <Typography 
                    component="a"
                    href={form.settings.redirectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none' }}
                  >
                    {form.settings.redirectUrl}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Public Link */}
          {form.isActive && (
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Public Form Link
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={handleEditUrl}
                >
                  Edit URL
                </Button>
              </Box>
              
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Custom URL
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography 
                    component="a"
                    href={form.customUrl ? `${window.location.origin}/${form.customUrl}` : `${window.location.origin}/form/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      flex: 1,
                      p: 2,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      textDecoration: 'none',
                      fontFamily: 'monospace',
                      color: 'primary.main'
                    }}
                  >
                    {window.location.origin}/{form.customUrl || `form/${id}`}
                  </Typography>
                  <IconButton
                    onClick={handleCopyLink}
                    size="small"
                    title={isClipboardAvailable() 
                      ? "Copy link to clipboard" 
                      : "Copy link (will show dialog)"
                    }
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<ShareIcon />}
                  onClick={handleCopyLink}
                  fullWidth
                >
                  Copy Link
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ViewIcon />}
                  href={form.customUrl ? `${window.location.origin}/${form.customUrl}` : `${window.location.origin}/form/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                >
                  Open Form
                </Button>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Form Preview */}
        <Box width={400}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Form Preview
            </Typography>
            
            <Box border={1} borderColor="divider" borderRadius={1} p={2} bgcolor="background.default">
              <Typography variant="h5" gutterBottom>
                {form.title}
              </Typography>
              
              {form.description && (
                <Typography variant="body2" color="textSecondary" paragraph>
                  {form.description}
                </Typography>
              )}

              {(form.settings?.requirePayment || form.settings?.requirePayment) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Payment required: ৳{form.settings?.amount || form.settings?.paymentAmount || 0}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {form.fields.length === 0 ? (
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  No fields added yet
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {/* Show admin view notification */}
                  {[
                    'super_admin', 'admin', 'form_manager', 'payment_approver',
                    'submission_viewer', 'submission_editor', 'notification_manager'
                  ].includes(user?.role || '') && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      Admin View: Showing all fields including restricted ones
                    </Alert>
                  )}
                  
                  {(() => {
                    // Filter fields based on user role and context
                    const adminRoles = [
                      'super_admin', 'admin', 'form_manager', 'payment_approver',
                      'submission_viewer', 'submission_editor', 'notification_manager'
                    ];
                    const isAdmin = adminRoles.includes(user?.role || '');
                    let fieldsToShow: FormField[];
                    
                    console.log('FormView field filtering:', {
                      userRole: user?.role,
                      isAdmin,
                      totalFields: form.fields.length,
                      fieldsWithVisibility: form.fields.filter(f => f.visibility?.level !== 'public').length
                    });
                    
                    if (isAdmin) {
                      // Admin sees all fields
                      fieldsToShow = form.fields;
                    } else {
                      // Public users only see fields they're allowed to view
                      fieldsToShow = filterFieldsByVisibility(form.fields, user, 'view');
                      console.log('Public user filtered fields:', {
                        originalCount: form.fields.length,
                        filteredCount: fieldsToShow.length,
                        hiddenFields: form.fields.filter(f => !fieldsToShow.includes(f)).map(f => ({
                          label: f.label,
                          visibility: f.visibility?.level
                        }))
                      });
                    }
                    
                    return fieldsToShow.map((field, index) => {
                      const visibilityInfo = getFieldVisibilityInfo(field);
                      const isRestricted = field.visibility?.level !== 'public';
                      
                      return (
                        <Box 
                          key={field.id}
                          sx={{
                            border: isAdmin && isRestricted ? '1px dashed orange' : 'none',
                            borderRadius: 1,
                            p: isAdmin && isRestricted ? 1 : 0
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography variant="body2">
                              {index + 1}. {field.label}
                              {field.required && <span style={{ color: 'red' }}> *</span>}
                            </Typography>
                            
                            {field.visibility?.level && field.visibility.level !== 'public' && (
                              <Chip
                                label={visibilityInfo.level}
                                size="small"
                                color={visibilityInfo.color}
                                sx={{ 
                                  fontSize: '0.65rem',
                                  height: '16px',
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            )}
                          </Box>
                          
                          <Typography variant="caption" color="textSecondary">
                            Type: {field.type}
                            {field.options && field.options.length > 0 && ` (${field.options.length} options)`}
                            {field.placeholder && ` • Placeholder: "${field.placeholder}"`}
                          </Typography>
                          
                          {isAdmin && isRestricted && (
                            <Typography variant="caption" color="warning.main" display="block" mt={0.5}>
                              ⚠️ This field is {field.visibility?.level === 'hidden' ? 'hidden from public users' : 'admin-only'}
                            </Typography>
                          )}
                          
                          {field.visibility?.level === 'admin-only' && field.visibility.publicCanView && (
                            <Typography variant="caption" color="info.main" display="block" mt={0.5}>
                              👁️ Public users can view but not edit this field
                            </Typography>
                          )}
                        </Box>
                      );
                    });
                  })()}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />
              
              <Box textAlign="center">
                <Button variant="contained" disabled fullWidth>
                  Submit Form
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Box mt={3}>
        <Typography variant="caption" color="textSecondary">
          Created: {new Date(form.createdAt).toLocaleString()} • 
          Last updated: {new Date(form.updatedAt).toLocaleString()}
        </Typography>
      </Box>

      {/* Edit URL Dialog */}
      <Dialog open={editUrlDialog} onClose={() => setEditUrlDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Custom URL</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Customize the public URL for your form. This URL will be used for public access.
          </Typography>
          <TextField
            fullWidth
            label="Custom URL"
            value={newCustomUrl}
            onChange={(e) => {
              setNewCustomUrl(e.target.value);
              setUrlError('');
            }}
            error={!!urlError}
            helperText={urlError || 'Only letters, numbers, hyphens, and underscores are allowed'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {window.location.origin}/
                </InputAdornment>
              ),
            }}
            placeholder="my-custom-form"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUrlDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveUrl} variant="contained">
            Save URL
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormView;
